import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { Auth } from './components/Auth';
import { Onboarding } from './components/Onboarding';
import { BowelMovements } from './pages/BowelMovements';
import { MedicationHistory } from './MedicationHistory';
import { MyMedications } from './MyMedications';
import { AddMedicationForm } from './AddMedicationForm';
import Today from './Today';
import { Wellbeing } from './pages/Wellbeing';
import { AddWellbeing } from './components/Wellbeing/AddWellbeing';
import { DiseaseStateReport } from './pages/DiseaseStateReport';
import { HealthReport } from './pages/HealthReport';
import { ReportsList } from './pages/ReportsList';
import { AccountPage } from './pages/AccountPage';
import { LabResultsPage } from './pages/LabResultsPage';
import { supabase } from './lib/supabase';
import { bowelMovementService } from './lib/bowelMovementService';
import { medicationService } from './lib/medicationService';
import { myMedicationService } from './lib/myMedicationService';
import { getUserPreferences, saveCurrentSection } from './lib/userPreferencesService';
import { ensureProfileExists, getProfile } from './lib/profileService';
import { BowelMovement, Medication, MyMedication } from './types';
import { HealthStatusStick } from './components/dashboard/SmartSummaryBadge';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { getErrorMessage } from './utils/errorMessages';
import { getTodayLocal } from './utils/dateHelpers';

// Ikony dla sidebara - responsive (mniejsze na mobile)
const iconSize = "w-5 h-5 md:w-6 md:h-6";
const BookIcon = () => (
  <svg className={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
  </svg>
);

const HeartIcon = () => (
  <svg className={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
  </svg>
);

const PillIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.5 20.5L3.5 13.5a5 5 0 117.07-7.07l9.9 9.9a5 5 0 11-7.07 7.07L10.5 20.5z"/>
    <path d="M8.5 8.5l7 7"/>
  </svg>
);

const BarChartIcon = () => (
  <svg className={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="20" x2="12" y2="10"/>
    <line x1="18" y1="20" x2="18" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="16"/>
  </svg>
);

const LabIcon = () => (
  <svg className={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 2v6l-4 12h14l-4-12V2"/>
    <line x1="9" y1="2" x2="15" y2="2"/>
  </svg>
);

type Section = 'account' | 'today' | 'bowel' | 'history' | 'reports' | 'reports-disease-state' | 'reports-flares' | 'medication-history' | 'my-medications' | 'my-medications-today' | 'add-medication' | 'wellbeing' | 'lab-results';

const UserIcon = () => (
  <svg className={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState<boolean | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  // Wczytaj zapisaną sekcję z localStorage jako fallback (dla użytkowników niezalogowanych)
  const getInitialSection = (): Section => {
    const path = window.location.pathname;
    if (path === '/wellbeing' || path === '/wellbeing/history') return 'wellbeing';
    if (path === '/lab-results') return 'lab-results';
    const saved = localStorage.getItem('currentSection');
    if (saved && ['today', 'bowel', 'history', 'reports', 'reports-disease-state', 'reports-flares', 'medication-history', 'my-medications', 'my-medications-today', 'add-medication', 'wellbeing', 'lab-results'].includes(saved)) {
      return saved as Section;
    }
    return 'today';
  };
  const [currentSection, setCurrentSection] = useState<Section>(getInitialSection());
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [bowelMovements, setBowelMovements] = useState<BowelMovement[]>([]);
  const [medications, setMedications] = useState<Medication[]>([]);
  const [myMedications, setMyMedications] = useState<MyMedication[]>([]);
  const [medicationHistoryFilter, setMedicationHistoryFilter] = useState<'all' | 'today' | 'week' | 'month' | 'quarter' | 'year'>('all');
  const [bowelMovementLoadError, setBowelMovementLoadError] = useState<Error | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showFABMenu, setShowFABMenu] = useState(false);
  const [medicationsToTakeCount, setMedicationsToTakeCount] = useState(0);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  // Responsywne breakpointy
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Synchronizacja ścieżki URL z sekcją (np. /wellbeing, /wellbeing/history)
  useEffect(() => {
    if (location.pathname === '/wellbeing' || location.pathname === '/wellbeing/history') {
      setCurrentSection('wellbeing');
    } else if (location.pathname === '/bowel') {
      setCurrentSection('bowel');
      navigate('/bowel');
    } else if (location.pathname === '/reports' || location.pathname === '/reports-disease-state' || location.pathname === '/reports-flares') {
      setCurrentSection('reports');
      navigate('/reports');
    } else if (location.pathname === '/history' || location.pathname === '/bowel-history' || location.pathname === '/wyproznieni') {
      setCurrentSection('history');
      navigate('/history');
    } else if (location.pathname === '/medication-history') {
      setCurrentSection('medication-history');
      navigate('/medication-history');
    } else if (location.pathname === '/my-medications') {
      setCurrentSection('my-medications');
      navigate('/my-medications');
    } else if (location.pathname === '/my-medications-today') {
      setCurrentSection('my-medications-today');
      navigate('/my-medications-today');
    } else if (location.pathname === '/add-medication') {
      setCurrentSection('add-medication');
      navigate('/add-medication');
    } else if (location.pathname === '/today') {
      setCurrentSection('today');
      navigate('/today');
    } else if (location.pathname === '/wellbeing') {
      setCurrentSection('wellbeing');
      navigate('/wellbeing');
    } else if (location.pathname === '/account') {
      setCurrentSection('account');
      navigate('/account');
    } else if (location.pathname === '/lab-results') {
      setCurrentSection('lab-results');
      navigate('/lab-results');
    } else {
      setCurrentSection('today');
      navigate('/today');
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Połączenie przywrócone');
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.error('Brak połączenia z internetem', { duration: Infinity });
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!isDesktop && sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.removeProperty('overflow');
    }
    return () => {
      document.body.style.removeProperty('overflow');
    };
  }, [isDesktop, sidebarOpen]);

  // Responsywne wartości
  const rem = (px: number) => `${px / 16}rem`;
  const clamp = (min: number, preferred: number, max: number) => 
    `clamp(${rem(min)}, ${preferred}vw, ${rem(max)})`;
  
  const sidebarWidth = isDesktop ? clamp(200, 15, 280) : '0';
  const headerPadding = isMobile ? rem(16) : isTablet ? rem(20) : clamp(20, 2, 32);
  const contentPadding = isMobile ? rem(16) : isTablet ? rem(20) : clamp(20, 2, 32);
  const borderRadius = rem(24);
  const fontSize = {
    xs: rem(10),
    sm: rem(12),
    base: rem(14),
    lg: rem(16),
    xl: rem(18),
    '2xl': rem(20),
    '3xl': clamp(24, 2.5, 32)
  };

  useEffect(() => {
    const loadSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      if (!session) {
        setTimeout(async () => {
          const { data: { session: retry } } = await supabase.auth.getSession();
          if (retry) setUser(retry.user);
        }, 500);
      }
    };
    loadSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Zapisz aktualną sekcję do Supabase (jeśli zalogowany) lub localStorage (fallback)
  useEffect(() => {
    if (user && preferencesLoaded) {
      // Zapisz do Supabase dla synchronizacji między urządzeniami
      saveCurrentSection(user.id, currentSection).catch(error => {
        console.error('Błąd zapisywania sekcji do Supabase:', error);
        // Fallback do localStorage w przypadku błędu
        localStorage.setItem('currentSection', currentSection);
      });
    } else {
      // Fallback do localStorage dla niezalogowanych użytkowników
      localStorage.setItem('currentSection', currentSection);
    }
  }, [currentSection, user, preferencesLoaded]);

  // Wczytaj profil – decyzja o onboardingu: brak profilu LUB onboarding_completed = false → onboarding
  useEffect(() => {
    if (!user?.id) {
      setDisplayName(null);
      setShowOnboarding(null);
      setIsLoadingProfile(false);
      return;
    }
    setIsLoadingProfile(true);
    const displayNameFromUser = user?.user_metadata?.full_name?.trim() || null;

    const loadProfile = async () => {
      let profile = await getProfile(user.id);
      const isNewUser = typeof window !== 'undefined' && sessionStorage.getItem('justRegisteredUserId') === user.id;
      if (!profile) {
        profile = await ensureProfileExists(user.id, displayNameFromUser, !isNewUser);
      }
      console.log('USER:', user);
      console.log('PROFIL Z BAZY:', profile);
      console.log('ONBOARDING COMPLETED:', profile?.onboarding_completed);
      setDisplayName(profile?.display_name?.trim() || null);
      // onboarding: brak profilu LUB onboarding_completed = false/null
      const needsOnboarding = !profile || !(profile.onboarding_completed === true);
      setShowOnboarding(needsOnboarding);
    };

    loadProfile()
      .catch((err) => {
        console.error('Błąd ładowania profilu:', err);
        setShowOnboarding(false);
      })
      .finally(() => setIsLoadingProfile(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.user_metadata?.full_name]);

  // Wczytaj preferencje użytkownika z Supabase przy zalogowaniu
  useEffect(() => {
    if (user) {
      loadBowelMovements();
      loadMedications();
      loadMyMedications();
      
      // Wczytaj preferencje użytkownika z Supabase
      getUserPreferences(user.id)
        .then(prefs => {
          if (prefs?.currentSection) {
            const validSection = ['today', 'bowel', 'history', 'reports', 'reports-disease-state', 'reports-flares', 'medication-history', 'my-medications', 'my-medications-today', 'add-medication', 'wellbeing', 'lab-results'].includes(prefs.currentSection);
            if (validSection) {
              setCurrentSection(prefs.currentSection as Section);
            }
          }
          setPreferencesLoaded(true);
        })
        .catch(error => {
          console.error('Błąd wczytywania preferencji z Supabase:', error);
          // Fallback: użyj localStorage
          const saved = localStorage.getItem('currentSection');
          if (saved && ['today', 'bowel', 'history', 'reports', 'reports-disease-state', 'reports-flares', 'medication-history', 'my-medications', 'my-medications-today', 'add-medication', 'wellbeing', 'lab-results'].includes(saved)) {
            setCurrentSection(saved as Section);
          }
          setPreferencesLoaded(true);
        });
      
      // Załaduj leki z bazy danych
      import('./data/medications').then(module => {
        module.loadMedications();
      });
    } else {
      // Użytkownik wylogowany - użyj localStorage
      setPreferencesLoaded(true);
    }
  }, [user]);

  const loadBowelMovements = async () => {
    try {
      setBowelMovementLoadError(null);
      const data = await bowelMovementService.getAll();
      setBowelMovements(data);
    } catch (error) {
      console.error('Błąd ładowania danych:', error);
      setBowelMovementLoadError(error instanceof Error ? error : new Error(String(error)));
    }
  };

  const loadMedications = async () => {
    try {
      const data = await medicationService.getAll();
      setMedications(data);
    } catch (error) {
      console.error('Błąd ładowania leków:', error);
    }
  };

  const loadMyMedications = async () => {
    try {
      const data = await myMedicationService.getAll();
      setMyMedications(data);
      myMedicationService.ensureMedicationsHaveSchedules().catch(err => console.error('ensureMedicationsHaveSchedules:', err));
    } catch (error) {
      console.error('Błąd ładowania moich leków:', error);
    }
  };

  // Liczba leków do wzięcia dzisiaj (dla badge w FAB)
  useEffect(() => {
    if (!user) return;
    const today = getTodayLocal();
    myMedicationService.getScheduleEntries(today, today, 'today')
      .then(entries => {
        const count = entries.filter(e => !e.taken).length;
        setMedicationsToTakeCount(count);
      })
      .catch(() => setMedicationsToTakeCount(0));
  }, [user, myMedications, showFABMenu]);

  const handleBowelMovementSubmit = async (data: BowelMovement) => {
    try {
      console.log('Próba zapisania wypróżnienia:', data);
      console.log('Użytkownik:', user?.id);
      
      if (data.id && bowelMovements.find(bm => bm.id === data.id)) {
        // Aktualizacja istniejącego wpisu
        console.log('Aktualizacja istniejącego wpisu:', data.id);
        await bowelMovementService.update(data.id, data);
      } else {
        // Dodanie nowego wpisu
        console.log('Dodawanie nowego wpisu');
        const created = await bowelMovementService.create(data);
        console.log('Wpis został zapisany:', created);
      }
      await loadBowelMovements();
      toast.success(data.id ? 'Wpis zaktualizowany' : 'Wypróżnienie dodane');
      setCurrentSection('history');
    } catch (error) {
      console.error('Błąd zapisywania danych:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const handleBowelMovementUpdate = async (id: string, data: BowelMovement) => {
    try {
      await bowelMovementService.update(id, data);
      await loadBowelMovements();
      toast.success('Wpis zaktualizowany');
    } catch (error) {
      console.error('Błąd aktualizacji danych:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const handleBowelMovementDelete = async (id: string) => {
    try {
      await bowelMovementService.delete(id);
      await loadBowelMovements();
      toast.success('Wpis usunięty', { duration: 2000 });
    } catch (error) {
      console.error('Błąd usuwania danych:', error);
      toast.error(getErrorMessage(error));
    }
  };

  // Usunięto handleMedicationSubmit - sekcja "Dodaj lek" została usunięta

  const handleMedicationUpdate = async (id: string, data: Medication) => {
    try {
      await medicationService.update(id, data);
      await loadMedications();
    } catch (error) {
      console.error('Błąd aktualizacji leku:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const handleMedicationDelete = async (id: string) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten wpis?')) {
      return;
    }
    try {
      await medicationService.delete(id);
      await loadMedications();
    } catch (error) {
      console.error('Błąd usuwania leku:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const handleMyMedicationAdd = async (data: MyMedication) => {
    try {
      console.log('Próba zapisania planu leczenia:', data);
      const created = await myMedicationService.create(data);
      console.log('Plan leczenia zapisany:', created);
      await loadMyMedications();
      
      // Automatycznie wygeneruj harmonogram od start_date leku do końca 2027 (dla tapering może być dłuższy)
      const now = new Date();
      const startDate = created.startDate || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const endDate = created.type === 'tapering' && created.taperingSchedules?.length
        ? (created.taperingSchedules.map((s: any) => s.endDate).filter(Boolean).sort().reverse()[0] || created.endDate || '2027-12-31')
        : '2027-12-31';
      
      await myMedicationService.generateScheduleEntries(created, startDate, endDate);
      toast.success('Lek dodany do listy');
    } catch (error) {
      console.error('Błąd dodawania planu leczenia:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const handleMyMedicationUpdate = async (id: string, data: MyMedication) => {
    try {
      await myMedicationService.update(id, data);
      await loadMyMedications();
      
      // Regeneruj harmonogram od start_date leku do końca 2027 (dla tapering: do ostatniego etapu)
      const now = new Date();
      const startDate = data.startDate || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const endDate = data.type === 'tapering' && data.taperingSchedules?.length
        ? (data.taperingSchedules.map((s: any) => s.endDate).filter(Boolean).sort().reverse()[0] || data.endDate || '2027-12-31')
        : '2027-12-31';
      
      await myMedicationService.generateScheduleEntries(data, startDate, endDate);
      toast.success('Lek zaktualizowany');
    } catch (error) {
      console.error('Błąd aktualizacji planu leczenia:', error);
      toast.error('Nie udało się zaktualizować leku. Spróbuj ponownie.');
    }
  };

  const handleMyMedicationDelete = async (id: string) => {
    try {
      await myMedicationService.delete(id);
      await loadMyMedications();
      toast.success('Lek usunięty', { duration: 2000 });
    } catch (error) {
      console.error('Błąd usuwania planu leczenia:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const handleNavigate = (section: string, params?: Record<string, string>) => {
    if (section === 'bowel') {
      setCurrentSection('bowel');
      navigate('/bowel');
    } else if (section === 'reports' || section === 'reports-disease-state' || section === 'reports-flares') {
      setCurrentSection(section);
      navigate('/reports');
    } else if (section === 'history' || section === 'bowel-history' || section === 'wyproznieni') {
      setCurrentSection('history');
      navigate('/history');
    } else if (section === 'medication-history') {
      setCurrentSection('medication-history');
      navigate('/medication-history');
    } else if (section === 'my-medications') {
      setCurrentSection('my-medications');
      navigate('/my-medications');
    } else if (section === 'my-medications-today') {
      setCurrentSection('my-medications-today');
      navigate('/my-medications-today');
    } else if (section === 'add-medication') {
      setCurrentSection('add-medication');
      navigate('/add-medication');
    } else if (section === 'today') {
      setCurrentSection('today');
      navigate('/today');
    } else if (section === 'wellbeing') {
      setCurrentSection('wellbeing');
      const query = params?.date ? `?date=${params.date}` : '';
      navigate(`/wellbeing${query}`);
    } else if (section === 'lab-results') {
      setCurrentSection('lab-results');
      navigate('/lab-results');
    } else {
      setCurrentSection('today');
      navigate('/today');
    }
  };

  const filterEntries = <T extends { date: string }>(entries: T[], filter: string): T[] => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return entries.filter(entry => {
      const entryDate = new Date(entry.date + 'T12:00:00');
      
      switch (filter) {
        case 'today':
          return entryDate >= today;
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return entryDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return entryDate >= monthAgo;
        case 'quarter':
          const quarterAgo = new Date(today);
          quarterAgo.setMonth(quarterAgo.getMonth() - 3);
          return entryDate >= quarterAgo;
        case 'year':
          const yearAgo = new Date(today);
          yearAgo.setFullYear(yearAgo.getFullYear() - 1);
          return entryDate >= yearAgo;
        default:
          return true;
      }
    });
  };

  // Redirect z maila (tokeny w hash) – przekieruj na /auth/callback do obsłużenia
  if (!user && typeof window !== 'undefined') {
    const hash = window.location.hash || '';
    if (/[#&]access_token=/.test(hash) || /[#&]refresh_token=/.test(hash)) {
      window.location.replace(`/auth/callback${window.location.hash}`);
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAFAFA' }}>
          <p style={{ color: '#64748b' }}>Potwierdzanie konta...</p>
        </div>
      );
    }
  }

  // Pokazuj ekran rejestracji jeśli nie ma użytkownika
  if (!user) {
    return (
      <div className="min-h-viewport" style={{ backgroundColor: '#FAFAFA', display: 'block' }}>
        {!isOnline && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              background: '#ef4444',
              color: 'white',
              padding: '12px',
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: 500,
              zIndex: 999999,
            }}
          >
            Brak połączenia z internetem. Niektóre funkcje mogą nie działać.
          </div>
        )}
        <div style={{ padding: rem(20) }}>
          <Auth onAuthSuccess={() => {}} />
        </div>
        <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      </div>
    );
  }

  // Profil się ładuje – nie podejmuj decyzji o onboardingu
  if (user && isLoadingProfile) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAFAFA' }}>
        <p style={{ color: '#64748b' }}>Ładowanie...</p>
      </div>
    );
  }

  // onboarding_completed = false → pokaż onboarding
  if (user && !isLoadingProfile && showOnboarding === true) {
    const userName = displayName || user?.user_metadata?.full_name?.trim() || user?.email?.split('@')[0] || '';
    return (
      <div className="min-h-viewport" style={{ minHeight: '100vh' }}>
        <Onboarding
          userName={userName}
          userId={user.id}
          onComplete={() => {
            sessionStorage.removeItem('justRegisteredUserId');
            setShowOnboarding(false);
            setCurrentSection('today');
            navigate('/today');
          }}
        />
      </div>
    );
  }

  // showOnboarding === null przed załadowaniem (safety)
  if (user && showOnboarding === null) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAFAFA' }}>
        <p style={{ color: '#64748b' }}>Ładowanie...</p>
      </div>
    );
  }

  // Wspólny layout z sidebar'em dla wszystkich stron
  const Sidebar = () => {
    const sidebarStyle: React.CSSProperties = {
      width: isDesktop ? sidebarWidth : rem(280),
      minWidth: rem(200),
      maxWidth: rem(280),
      background: '#FFFFFF',
      borderRight: '1px solid #E5E5E5',
      display: 'flex',
      flexDirection: 'column',
      padding: `${rem(24)} ${rem(16)}`,
      flexShrink: 0,
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      height: '100vh',
      overflowY: 'auto',
      position: isDesktop ? 'relative' : 'fixed',
      top: 0,
      left: 0,
      zIndex: 1000,
      transform: isDesktop ? 'none' : sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
      transition: 'transform 0.3s ease-in-out'
    };

    return (
      <>
        {/* Overlay dla mobile */}
        {!isDesktop && sidebarOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
              cursor: 'pointer'
            }}
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <div style={sidebarStyle}>
          {/* Przycisk zamknij dla mobile */}
          {!isDesktop && (
            <button
              onClick={() => setSidebarOpen(false)}
              style={{
                position: 'absolute',
                top: rem(16),
                right: rem(16),
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: rem(8),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              aria-label="Zamknij menu"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
          <div style={{
            fontSize: fontSize['2xl'],
            fontWeight: 'var(--font-weight-medium)',
            color: '#1A1A1A',
            marginBottom: rem(32),
            paddingLeft: rem(8),
            paddingRight: !isDesktop ? rem(40) : rem(8)
          }}>
            IBD Tracker
          </div>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: rem(8) }}>
          <button
            onClick={() => {
              if (location.pathname === '/account') navigate('/');
              handleNavigate('today');
              if (!isDesktop) setSidebarOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(12),
              padding: `${rem(12)} ${rem(16)}`,
              minHeight: !isDesktop ? rem(44) : undefined,
              background: currentSection === 'today' ? '#F5F5F5' : 'transparent',
              border: 'none',
              borderRadius: rem(12),
              cursor: 'pointer',
              color: currentSection === 'today' ? '#1A1A1A' : '#999999',
              fontSize: fontSize.base,
              fontWeight: 'var(--font-weight-medium)',
              textAlign: 'left'
            }}
          >
            <BarChartIcon />
            <span>Dzisiaj</span>
          </button>
          <button
            onClick={() => {
              if (location.pathname === '/account') navigate('/');
              handleNavigate('history');
              if (!isDesktop) setSidebarOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(12),
              padding: `${rem(12)} ${rem(16)}`,
              minHeight: !isDesktop ? rem(44) : undefined,
              background: currentSection === 'history' ? '#F5F5F5' : 'transparent',
              border: 'none',
              borderRadius: rem(12),
              cursor: 'pointer',
              color: currentSection === 'history' ? '#1A1A1A' : '#999999',
              fontSize: fontSize.base,
              fontWeight: 'var(--font-weight-medium)',
              textAlign: 'left'
            }}
          >
            <BookIcon />
            <span>Wypróżnienia</span>
          </button>
          <button
            onClick={() => {
              if (location.pathname === '/account') navigate('/');
              handleNavigate('wellbeing');
              if (!isDesktop) setSidebarOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(12),
              padding: `${rem(12)} ${rem(16)}`,
              minHeight: !isDesktop ? rem(44) : undefined,
              background: currentSection === 'wellbeing' ? '#F5F5F5' : 'transparent',
              border: 'none',
              borderRadius: rem(12),
              cursor: 'pointer',
              color: currentSection === 'wellbeing' ? '#1A1A1A' : '#999999',
              fontSize: fontSize.base,
              fontWeight: 'var(--font-weight-medium)',
              textAlign: 'left'
            }}
          >
            <HeartIcon />
            <span>Samopoczucie</span>
          </button>
          <button
            onClick={() => {
              if (location.pathname === '/account') navigate('/');
              handleNavigate('my-medications');
              if (!isDesktop) setSidebarOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(12),
              padding: `${rem(12)} ${rem(16)}`,
              minHeight: !isDesktop ? rem(44) : undefined,
              background: currentSection === 'my-medications' || currentSection === 'my-medications-today' ? '#F5F5F5' : 'transparent',
              border: 'none',
              borderRadius: rem(12),
              cursor: 'pointer',
              color: currentSection === 'my-medications' || currentSection === 'my-medications-today' ? '#1A1A1A' : '#999999',
              fontSize: fontSize.base,
              fontWeight: 'var(--font-weight-medium)',
              textAlign: 'left'
            }}
          >
            <PillIcon />
            <span>Leki</span>
          </button>
          <button
            onClick={() => {
              if (location.pathname === '/account') navigate('/');
              handleNavigate('reports');
              if (!isDesktop) setSidebarOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(12),
              padding: `${rem(12)} ${rem(16)}`,
              minHeight: !isDesktop ? rem(44) : undefined,
              background: currentSection === 'reports' ? '#F5F5F5' : 'transparent',
              border: 'none',
              borderRadius: rem(12),
              cursor: 'pointer',
              color: currentSection === 'reports' ? '#1A1A1A' : '#999999',
              fontSize: fontSize.base,
              fontWeight: 'var(--font-weight-medium)',
              textAlign: 'left'
            }}
          >
            <BarChartIcon />
            <span>Raporty</span>
          </button>
          <button
            onClick={() => {
              if (location.pathname === '/account') navigate('/');
              handleNavigate('lab-results');
              if (!isDesktop) setSidebarOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(12),
              padding: `${rem(12)} ${rem(16)}`,
              minHeight: !isDesktop ? rem(44) : undefined,
              background: currentSection === 'lab-results' ? '#F5F5F5' : 'transparent',
              border: 'none',
              borderRadius: rem(12),
              cursor: 'pointer',
              color: currentSection === 'lab-results' ? '#1A1A1A' : '#999999',
              fontSize: fontSize.base,
              fontWeight: 'var(--font-weight-medium)',
              textAlign: 'left'
            }}
          >
            <LabIcon />
            <span>Wyniki badań</span>
          </button>
          <button
            onClick={() => {
              navigate('/account');
              if (!isDesktop) setSidebarOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: rem(12),
              padding: `${rem(12)} ${rem(16)}`,
              minHeight: !isDesktop ? rem(44) : undefined,
              background: location.pathname === '/account' ? '#F5F5F5' : 'transparent',
              border: 'none',
              borderRadius: rem(12),
              cursor: 'pointer',
              color: location.pathname === '/account' ? '#1A1A1A' : '#999999',
              fontSize: fontSize.base,
              fontWeight: 'var(--font-weight-medium)',
              textAlign: 'left'
            }}
          >
            <UserIcon />
            <span>Moje konto</span>
          </button>
        </nav>
        <div style={{ marginTop: 'auto', paddingTop: rem(24) }}>
          <button 
            onClick={async () => {
              try {
                await supabase.auth.signOut();
                window.location.reload();
              } catch (error) {
                console.error('Błąd wylogowania:', error);
                window.location.reload();
              }
            }}
            style={{
              width: '100%',
              padding: `${rem(12)} ${rem(16)}`,
              background: 'transparent',
              border: '1px solid #E5E5E5',
              borderRadius: rem(12),
              cursor: 'pointer',
              color: '#999999',
              fontSize: fontSize.base,
              fontWeight: 'var(--font-weight-medium)',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: rem(12)
            }}
          >
            <span>Wyloguj</span>
          </button>
        </div>
      </div>
      </>
    );
  };

  // Jeśli użytkownik jest zalogowany, pokaż pełną aplikację
  // Dla sekcji 'today' - pełny ekran z sidebar'em (ale nie gdy jesteśmy na /account)
  if (currentSection === 'today' && location.pathname !== '/account') {
    const rawName = displayName || user?.user_metadata?.full_name?.trim() || user?.email?.split('@')[0] || 'Użytkowniku';
    const capitalizedName = rawName ? rawName.charAt(0).toUpperCase() + rawName.slice(1) : 'Użytkowniku';
    
    return (
      <div
        className="app-layout app-layout-today"
        style={{
          display: 'flex',
          flexDirection: isDesktop ? 'row' : 'column',
          background: '#FAFAFA',
          overflow: 'hidden',
          width: '100%'
        }}
      >
        {!isOnline && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              background: '#ef4444',
              color: 'white',
              padding: '12px',
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: 500,
              zIndex: 999999,
            }}
          >
            Brak połączenia z internetem. Niektóre funkcje mogą nie działać.
          </div>
        )}
        <Sidebar />
        <div
          className="app-layout-main"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 0
          }}
        >
          {/* Header - Banner jak w sekcji Dzisiaj */}
          <div style={{
            padding: `${headerPadding} ${contentPadding}`,
            flexShrink: 0,
            background: '#FFFFFF',
            borderBottom: '1px solid #E5E5E5',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            {/* Hamburger button dla mobile */}
            {!isDesktop && (
              <button
                onClick={() => setSidebarOpen(true)}
                style={{
                  position: 'absolute',
                  top: headerPadding,
                  left: contentPadding,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: rem(8),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1001
                }}
                aria-label="Otwórz menu"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              </button>
            )}
            <div style={{ paddingLeft: !isDesktop ? rem(48) : 0 }}>
              <h1 style={{
                fontSize: fontSize['3xl'],
                fontWeight: 'var(--font-weight-medium)',
                color: '#1A1A1A',
                margin: `0 0 ${rem(4)} 0`
              }}>
                Witaj, {capitalizedName}
              </h1>
              <div style={{
                fontSize: fontSize.base,
                color: '#999999'
              }}>
                {new Date().toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            </div>
            <HealthStatusStick recentMovements={bowelMovements} />
          </div>
          <div className="page-content-scroll" style={{ flex: 1, overflow: 'auto' }}>
            <Today
              user={user}
              myMedications={myMedications}
              bowelMovements={bowelMovements}
              onNavigate={handleNavigate}
              onReload={() => {
                loadBowelMovements();
                loadMyMedications();
              }}
            />
          </div>
        </div>
        {/* FAB - Premium */}
        {showFABMenu && (
          <div
            onClick={() => setShowFABMenu(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.4)',
              backdropFilter: 'blur(4px)',
              zIndex: 999997,
              animation: 'fadeIn 0.2s ease-out',
            }}
          />
        )}
        {showFABMenu && (
          <div
            style={{
              position: 'fixed',
              bottom: '140px',
              right: '20px',
              background: 'white',
              borderRadius: '20px',
              padding: '12px',
              boxShadow: '0 20px 60px rgba(15, 23, 42, 0.15), 0 0 1px rgba(15, 23, 42, 0.1)',
              zIndex: 999998,
              minWidth: '240px',
              animation: 'slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div style={{ padding: '8px 8px 12px', fontSize: '13px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Szybkie akcje
            </div>
            <button
              onClick={() => { setShowFABMenu(false); handleNavigate('bowel'); }}
              style={{
                width: '100%',
                padding: '16px',
                border: 'none',
                background: 'transparent',
                borderRadius: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                fontSize: '15px',
                fontWeight: '500',
                color: '#1e293b',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                marginBottom: '4px',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.transform = 'translateX(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = 'translateX(0)'; }}
            >
              <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
                </svg>
              </div>
              <div style={{ textAlign: 'left', flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '15px', color: '#0f172a' }}>Wypróżnienie</div>
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>Dodaj nowy wpis</div>
              </div>
            </button>
            <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #e2e8f0, transparent)', margin: '4px 12px' }} />
            <button
              onClick={() => { setShowFABMenu(false); handleNavigate('wellbeing'); }}
              style={{
                width: '100%',
                padding: '16px',
                border: 'none',
                background: 'transparent',
                borderRadius: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                fontSize: '15px',
                fontWeight: '500',
                color: '#1e293b',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                marginBottom: '4px',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.transform = 'translateX(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = 'translateX(0)'; }}
            >
              <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
                  <path d="M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27"/>
                </svg>
              </div>
              <div style={{ textAlign: 'left', flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '15px', color: '#0f172a' }}>Samopoczucie</div>
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>Dzienny check-in</div>
              </div>
            </button>
            <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #e2e8f0, transparent)', margin: '4px 12px' }} />
            <button
              onClick={() => { setShowFABMenu(false); handleNavigate('my-medications-today'); }}
              style={{
                width: '100%',
                padding: '16px',
                border: 'none',
                background: 'transparent',
                borderRadius: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                fontSize: '15px',
                fontWeight: '500',
                color: '#1e293b',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.transform = 'translateX(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = 'translateX(0)'; }}
            >
              <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"/>
                  <path d="m8.5 8.5 7 7"/>
                </svg>
                {medicationsToTakeCount > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    background: '#ef4444',
                    color: 'white',
                    borderRadius: '10px',
                    padding: '2px 6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    minWidth: '20px',
                    textAlign: 'center',
                  }}>
                    {medicationsToTakeCount}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'left', flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '15px', color: '#0f172a' }}>Dzisiejsze leki</div>
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>Odhacz przyjęte</div>
              </div>
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={() => setShowFABMenu(!showFABMenu)}
          aria-label="Dodaj wpis"
          style={{
            position: 'fixed',
            bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))',
            right: '1.25rem',
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: showFABMenu ? '#5A6EE6' : 'linear-gradient(135deg, #6B7FFF 0%, #5A6EE6 100%)',
            border: 'none',
            boxShadow: showFABMenu ? '0 8px 24px rgba(107, 127, 255, 0.25)' : '0 8px 24px rgba(107, 127, 255, 0.35)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            transform: showFABMenu ? 'rotate(135deg) scale(0.95)' : 'rotate(0deg) scale(1)',
          }}
          onMouseEnter={(e) => {
            if (!showFABMenu) {
              e.currentTarget.style.transform = 'scale(1.08)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(107, 127, 255, 0.45)';
            }
          }}
          onMouseLeave={(e) => {
            if (!showFABMenu) {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(107, 127, 255, 0.35)';
            }
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      </div>
    );
  }

  const showFAB = location.pathname !== '/account';

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#FAFAFA',
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      width: '100%',
      overflow: 'hidden'
    }}>
      {!isOnline && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            background: '#ef4444',
            color: 'white',
            padding: '12px',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 500,
            zIndex: 999999,
          }}
        >
          Brak połączenia z internetem. Niektóre funkcje mogą nie działać.
        </div>
      )}
      <Sidebar />
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'hidden',
        height: '100vh'
      }}>
        {/* Header z hamburger button dla mobile */}
        <div style={{
          padding: `${headerPadding} ${contentPadding}`,
          flexShrink: 0,
          background: '#FFFFFF',
          borderBottom: '1px solid #E5E5E5',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          position: 'relative'
        }}>
          {!isDesktop && (
            <button
              onClick={() => setSidebarOpen(true)}
              style={{
                position: 'absolute',
                top: headerPadding,
                left: contentPadding,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: rem(8),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1001
              }}
              aria-label="Otwórz menu"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
          )}
          <div style={{ paddingLeft: !isDesktop ? rem(48) : 0, display: 'flex', alignItems: 'center', gap: rem(12) }}>
            {location.pathname === '/account' && (
              <button
                onClick={() => navigate('/')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: rem(8),
                  display: 'flex',
                  alignItems: 'center',
                  color: '#666',
                  fontSize: fontSize.base
                }}
                aria-label="Wróć"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                <span style={{ marginLeft: rem(4) }}>Wstecz</span>
              </button>
            )}
            <h1 style={{
              fontSize: fontSize['2xl'],
              fontWeight: 'var(--font-weight-medium)',
              color: '#1A1A1A',
              margin: 0
            }}>
              {location.pathname === '/account' ? 'Moje konto' :
               currentSection === 'lab-results' ? 'Wyniki badań' :
               currentSection === 'history' ? 'Wypróżnienia' :
               currentSection === 'wellbeing' ? 'Samopoczucie' :
               currentSection === 'my-medications' || currentSection === 'my-medications-today' ? 'Leki' :
               currentSection === 'reports' ? 'Raporty' :
               currentSection === 'reports-disease-state' ? 'Stan choroby' :
               currentSection === 'reports-flares' ? 'Zaostrzenia' :
               currentSection === 'bowel' ? 'Wypróżnienia' :
               'IBD Tracker'}
            </h1>
          </div>
        </div>
        <div
          className="page-content-scroll"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: contentPadding
          }}
        >
        <div style={{ 
          maxWidth: '100%',
          width: '100%',
          backgroundColor: '#FFFFFF',
          borderRadius: borderRadius,
          padding: rem(24),
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          border: '1px solid #E5E5E5'
        }}>

        {/* Content */}
        {location.pathname === '/account' ? (
          <AccountPage
            user={user}
            rem={rem}
            fontSize={fontSize}
            borderRadius={borderRadius}
            onScheduleFixed={loadMyMedications}
          />
        ) : (
        <>
        {currentSection === 'lab-results' && (
          <LabResultsPage onBack={() => navigate('/')} />
        )}
        {currentSection === 'history' && (
          <BowelMovements
            entries={bowelMovements}
            onSubmit={handleBowelMovementSubmit}
            onUpdate={handleBowelMovementUpdate}
            onDelete={handleBowelMovementDelete}
            loadError={bowelMovementLoadError}
            onRetry={loadBowelMovements}
          />
        )}

        {currentSection === 'bowel' && (
          <BowelMovements
            entries={bowelMovements}
            onSubmit={handleBowelMovementSubmit}
            onUpdate={handleBowelMovementUpdate}
            onDelete={handleBowelMovementDelete}
            loadError={bowelMovementLoadError}
            onRetry={loadBowelMovements}
          />
        )}

        {currentSection === 'add-medication' && (
          <div>
            {/* Przyciski nawigacyjne */}
            <div className="mb-6 p-6">
              <div className="flex gap-2 flex-wrap">
                {/* Dzisiaj */}
                <button
                  onClick={() => handleNavigate('today')}
                  className="px-5 py-2.5 rounded-2xl cursor-pointer text-sm font-medium transition-all duration-200 flex items-center gap-2 bg-white text-[#999999] border border-[#F0F0F0] hover:border-[#E0E7FF] hover:text-[#6B7FFF]"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Dzisiaj
                </button>
                
                {/* Harmonogram */}
                <button
                  onClick={() => handleNavigate('my-medications')}
                  className="px-5 py-2.5 rounded-2xl cursor-pointer text-sm font-medium transition-all duration-200 flex items-center gap-2 bg-white text-[#999999] border border-[#F0F0F0] hover:border-[#E0E7FF] hover:text-[#6B7FFF]"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Harmonogram
                </button>
                
                {/* Moje leczenie */}
                <button
                  onClick={() => handleNavigate('my-medications')}
                  className="px-5 py-2.5 rounded-2xl cursor-pointer text-sm font-medium transition-all duration-200 flex items-center gap-2 bg-white text-[#999999] border border-[#F0F0F0] hover:border-[#E0E7FF] hover:text-[#6B7FFF]"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Dodaj leczenie
                </button>
                
                {/* Moje leki */}
                <button
                  onClick={() => handleNavigate('my-medications')}
                  className="px-5 py-2.5 rounded-2xl cursor-pointer text-sm font-medium transition-all duration-200 flex items-center gap-2 bg-white text-[#999999] border border-[#F0F0F0] hover:border-[#E0E7FF] hover:text-[#6B7FFF]"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                  Moje leki
                </button>
                
                {/* Dodaj lek */}
                <button
                  className="px-5 py-2.5 rounded-2xl cursor-pointer text-sm font-medium text-white shadow-sm transition-all duration-200 flex items-center gap-2 bg-gradient-to-r from-[#6B7FFF] to-[#8B5CF6]"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Dodaj lek
                </button>
              </div>
            </div>
            
            <AddMedicationForm 
              onSubmit={async () => {
                const { refreshMedications } = await import('./data/medications');
                await refreshMedications();
                setCurrentSection('my-medications');
              }}
              onCancel={() => setCurrentSection('today')}
            />
          </div>
        )}

        {currentSection === 'medication-history' && (
          <div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: rem(20),
              flexWrap: 'wrap',
              gap: rem(12)
            }}>
              <h2 style={{ margin: 0, color: '#1A1A1A', fontSize: fontSize['2xl'] }}>Historia leków</h2>
              <select
                value={medicationHistoryFilter}
                onChange={(e) => setMedicationHistoryFilter(e.target.value as any)}
                style={{
                  padding: `${rem(8)} ${rem(12)}`,
                  borderRadius: rem(8),
                  border: '1px solid #E5E5E5',
                  fontSize: fontSize.base,
                  background: '#FFFFFF',
                  color: '#1A1A1A',
                  cursor: 'pointer'
                }}
              >
                <option value="all">Wszystkie</option>
                <option value="today">Dzisiaj</option>
                <option value="week">Ten tydzień</option>
                <option value="month">Ten miesiąc</option>
                <option value="quarter">Ten kwartał</option>
                <option value="year">Ten rok</option>
              </select>
            </div>
            <MedicationHistory
              entries={filterEntries(medications, medicationHistoryFilter)}
              onUpdate={handleMedicationUpdate}
              onDelete={handleMedicationDelete}
              filter={medicationHistoryFilter}
            />
          </div>
        )}

        {currentSection === 'reports' && (
          <ReportsList onSelectReport={(reportId) => {
            if (reportId === 'disease-state') {
              handleNavigate('reports-disease-state');
            } else if (reportId === 'flares') {
              handleNavigate('reports-flares');
            }
          }} />
        )}
        
        {currentSection === 'reports-disease-state' && user && (
          <DiseaseStateReport
            user={user}
            onBack={() => handleNavigate('reports')}
            onNavigate={handleNavigate}
          />
        )}

        {currentSection === 'reports-flares' && user && (
          <HealthReport
            user={user}
            onBack={() => handleNavigate('reports')}
            onNavigate={handleNavigate}
          />
        )}

        {currentSection === 'my-medications' && (
          <MyMedications
            myMedications={myMedications}
            onAdd={handleMyMedicationAdd}
            onUpdate={handleMyMedicationUpdate}
            onDelete={handleMyMedicationDelete}
            onNavigate={handleNavigate}
            userId={user?.id}
          />
        )}

        {currentSection === 'my-medications-today' && (
          <MyMedications
            myMedications={myMedications}
            onAdd={handleMyMedicationAdd}
            onUpdate={handleMyMedicationUpdate}
            onDelete={handleMyMedicationDelete}
            onNavigate={handleNavigate}
            userId={user?.id}
          />
        )}

        {currentSection === 'wellbeing' && (
          location.pathname === '/wellbeing/history' ? (
            <Wellbeing />
          ) : (
            <AddWellbeing />
          )
        )}
        </>
        )}
        </div>
        </div>
      </div>
      {showFAB && (
        <>
          {showFABMenu && (
            <div
              onClick={() => setShowFABMenu(false)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15, 23, 42, 0.4)',
                backdropFilter: 'blur(4px)',
                zIndex: 999997,
                animation: 'fadeIn 0.2s ease-out',
              }}
            />
          )}
          {showFABMenu && (
            <div
              style={{
                position: 'fixed',
                bottom: '140px',
                right: '20px',
                background: 'white',
                borderRadius: '20px',
                padding: '12px',
                boxShadow: '0 20px 60px rgba(15, 23, 42, 0.15), 0 0 1px rgba(15, 23, 42, 0.1)',
                zIndex: 999998,
                minWidth: '240px',
                animation: 'slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              }}
            >
              <div style={{ padding: '8px 8px 12px', fontSize: '13px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Szybkie akcje
              </div>
              <button
                onClick={() => { setShowFABMenu(false); handleNavigate('bowel'); }}
                style={{
                  width: '100%',
                  padding: '16px',
                  border: 'none',
                  background: 'transparent',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  fontSize: '15px',
                  fontWeight: '500',
                  color: '#1e293b',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  marginBottom: '4px',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.transform = 'translateX(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = 'translateX(0)'; }}
              >
                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
                  </svg>
                </div>
                <div style={{ textAlign: 'left', flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '15px', color: '#0f172a' }}>Wypróżnienie</div>
                  <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>Dodaj nowy wpis</div>
                </div>
              </button>
              <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #e2e8f0, transparent)', margin: '4px 12px' }} />
              <button
                onClick={() => { setShowFABMenu(false); handleNavigate('wellbeing'); }}
                style={{
                  width: '100%',
                  padding: '16px',
                  border: 'none',
                  background: 'transparent',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  fontSize: '15px',
                  fontWeight: '500',
                  color: '#1e293b',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  marginBottom: '4px',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.transform = 'translateX(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = 'translateX(0)'; }}
              >
                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
                    <path d="M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27"/>
                  </svg>
                </div>
                <div style={{ textAlign: 'left', flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '15px', color: '#0f172a' }}>Samopoczucie</div>
                  <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>Dzienny check-in</div>
                </div>
              </button>
              <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #e2e8f0, transparent)', margin: '4px 12px' }} />
              <button
                onClick={() => { setShowFABMenu(false); handleNavigate('my-medications-today'); }}
                style={{
                  width: '100%',
                  padding: '16px',
                  border: 'none',
                  background: 'transparent',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  fontSize: '15px',
                  fontWeight: '500',
                  color: '#1e293b',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.transform = 'translateX(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = 'translateX(0)'; }}
              >
                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"/>
                    <path d="m8.5 8.5 7 7"/>
                  </svg>
                  {medicationsToTakeCount > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      background: '#ef4444',
                      color: 'white',
                      borderRadius: '10px',
                      padding: '2px 6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      minWidth: '20px',
                      textAlign: 'center',
                    }}>
                      {medicationsToTakeCount}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'left', flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '15px', color: '#0f172a' }}>Dzisiejsze leki</div>
                  <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>Odhacz przyjęte</div>
                </div>
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={() => setShowFABMenu(!showFABMenu)}
            aria-label="Dodaj wpis"
            style={{
              position: 'fixed',
              bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))',
              right: '1.25rem',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: showFABMenu ? '#5A6EE6' : 'linear-gradient(135deg, #6B7FFF 0%, #5A6EE6 100%)',
              border: 'none',
              boxShadow: showFABMenu ? '0 8px 24px rgba(107, 127, 255, 0.25)' : '0 8px 24px rgba(107, 127, 255, 0.35)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 999999,
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              transform: showFABMenu ? 'rotate(135deg) scale(0.95)' : 'rotate(0deg) scale(1)',
            }}
            onMouseEnter={(e) => {
              if (!showFABMenu) {
                e.currentTarget.style.transform = 'scale(1.08)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(107, 127, 255, 0.45)';
              }
            }}
            onMouseLeave={(e) => {
              if (!showFABMenu) {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(107, 127, 255, 0.35)';
              }
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </>
      )}

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: 'white',
            color: '#0f172a',
            borderRadius: '16px',
            padding: '16px 20px',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
            fontSize: '15px',
            fontWeight: '500',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: 'white',
            },
            style: {
              background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
              color: '#065f46',
              border: '2px solid #10b981',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: 'white',
            },
            style: {
              background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
              color: '#7f1d1d',
              border: '2px solid #ef4444',
            },
          },
          loading: {
            iconTheme: {
              primary: '#6B7FFF',
              secondary: 'white',
            },
          },
        }}
      />
    </div>
  );
}

export default App;
